import { useMemo } from 'react';
import TargetSkillIcon from '../assets/CheckpointIcon/TargetSkillIcon';
import PreSkillIcon from '../assets/CheckpointIcon/PreSkillIcon';
import { TargetSkillTypeLabel } from '../models/TargetSkillType';
import ExtensionSkillIcon from '../assets/CheckpointIcon/ExtensionSkillIcon';

interface UseSkillIconProps {
  targetSkillType?: string | null;
  className?: string;
}

interface SkillIconResult {
  IconComponent: () => JSX.Element;
  useCheckpointIcon: boolean;
  tooltipText: string;
}

/**
 * Custom hook to determine the appropriate skill icon based on targetSkillType
 * Returns the icon component, flag for checkpoint icon usage, and tooltip text
 */
export const useSkillIcon = ({ targetSkillType, className = 'icon-md' }: UseSkillIconProps): SkillIconResult => {
  return useMemo(() => {
    const useCheckpointIcon = targetSkillType === 'Target' || targetSkillType === 'Unit';
    const useExtensionIcon = targetSkillType === 'Extension';

    const IconComponent = () => {
      if (!targetSkillType) {
        return <PreSkillIcon />;
      }

      return (
        <div
          className={className}
          data-pr-tooltip={
            targetSkillType !== TargetSkillTypeLabel.NOT_ON_A_ROADMAP
              ? `${targetSkillType} Skill`
              : `Not on the selected Roadmap`
          }
        >
          {useCheckpointIcon ? <TargetSkillIcon /> : useExtensionIcon ? <ExtensionSkillIcon /> : <PreSkillIcon />}
        </div>
      );
    };

    const tooltipText =
      targetSkillType !== TargetSkillTypeLabel.NOT_ON_A_ROADMAP
        ? `${targetSkillType} Skill`
        : `Not on the selected Roadmap`;

    return {
      IconComponent,
      useCheckpointIcon,
      tooltipText,
    };
  }, [targetSkillType, className]);
};
